/**
 * Landing theme constants for the organizer frontend.
 * Used by the theme picker components to display preview swatches, font samples, etc.
 * Mirrors the backend LandingTheme model definitions.
 */

// ─── Types ─────────────────────────────────────────────────────────────────────

export interface ThemeTemplate {
  id: string;
  name: string;
  description: string;
  sections: string[];
}

export interface ThemePalette {
  id: string;
  name: string;
  description: string;
  /** Preview hex colors for swatch display [primary, accent, secondary, background] */
  previewSwatches: string[];
}

export interface ThemeFontPairing {
  id: string;
  name: string;
  description: string;
  headingFont: string;
  bodyFont: string;
  googleFontsUrl: string;
}

// ─── Templates ─────────────────────────────────────────────────────────────────

export const TEMPLATES: ThemeTemplate[] = [
  {
    id: 'classic',
    name: 'Classic',
    description: 'Traditional centered layout with elegant card sections.',
    sections: ['hero', 'media', 'schedule', 'rsvp', 'footer'],
  },
  {
    id: 'elegant',
    name: 'Elegant',
    description: 'Refined layout with serif accents and spacious sections.',
    sections: ['hero', 'media', 'schedule', 'rsvp', 'footer'],
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'Bold contemporary design with geometric accents.',
    sections: ['hero', 'media', 'schedule', 'rsvp', 'footer'],
  },
  {
    id: 'festive',
    name: 'Festive',
    description: 'Fun and playful with rounded elements and lively feel.',
    sections: ['hero', 'media', 'schedule', 'rsvp', 'footer'],
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Clean and understated with generous whitespace.',
    sections: ['hero', 'media', 'schedule', 'rsvp', 'footer'],
  },
];

// ─── Color Palettes ────────────────────────────────────────────────────────────

export const PALETTES: ThemePalette[] = [
  {
    id: 'warm_default',
    name: 'Warm Default',
    description: 'Friendly orange with warm tones.',
    previewSwatches: ['#F26522', '#F5C842', '#E8F5F0', '#FDF8F4'],
  },
  {
    id: 'classic_elegance',
    name: 'Classic Elegance',
    description: 'Timeless navy and gold.',
    previewSwatches: ['#1E3A5F', '#D4A520', '#F5F0E8', '#FAFAF7'],
  },
  {
    id: 'garden_party',
    name: 'Garden Party',
    description: 'Fresh greens and blush pink.',
    previewSwatches: ['#2D7D46', '#F4A0B2', '#F0F7F2', '#FDFAF5'],
  },
  {
    id: 'modern_minimal',
    name: 'Modern Minimal',
    description: 'Sleek dark gray with clean lines.',
    previewSwatches: ['#333333', '#888888', '#F5F5F5', '#FAFAFA'],
  },
  {
    id: 'tropical',
    name: 'Tropical',
    description: 'Vibrant teal and warm coral.',
    previewSwatches: ['#008B8B', '#FF6B4A', '#E8F5F4', '#FFF8F0'],
  },
  {
    id: 'rustic',
    name: 'Rustic',
    description: 'Earthy terracotta and sage green.',
    previewSwatches: ['#B85C38', '#8DAA6C', '#F5F0EB', '#FDFBF7'],
  },
  {
    id: 'royal',
    name: 'Royal',
    description: 'Deep purple with gold accents.',
    previewSwatches: ['#5B2D8E', '#D4A520', '#F2EEF8', '#FAF8FD'],
  },
  {
    id: 'night_sky',
    name: 'Night Sky',
    description: 'Dark mode with deep navy and starlight.',
    previewSwatches: ['#1A2744', '#6B8FD4', '#C4AA60', '#E5E2DD'],
  },
];

export const TEMPLATE_PALETTE_SUGGESTIONS: Record<string, string> = {
  classic: 'classic_elegance',
  elegant: 'classic_elegance',
  modern: 'modern_minimal',
  festive: 'tropical',
  minimal: 'modern_minimal',
};

// ─── Font Pairings ─────────────────────────────────────────────────────────────

export const FONT_PAIRINGS: ThemeFontPairing[] = [
  {
    id: 'playful',
    name: 'Playful',
    description: 'Friendly and approachable — the default look.',
    headingFont: 'Nunito',
    bodyFont: 'Inter',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Nunito:wght@700&family=Inter:wght@400&display=swap',
  },
  {
    id: 'classic',
    name: 'Classic',
    description: 'Elegant serif headings with clean body text.',
    headingFont: 'Playfair Display',
    bodyFont: 'Lato',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Lato:wght@400&display=swap',
  },
  {
    id: 'elegant',
    name: 'Elegant',
    description: 'Refined serif pairing — sophisticated and luxurious.',
    headingFont: 'Cormorant Garamond',
    bodyFont: 'Proza Libre',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@700&family=Proza+Libre:wght@400&display=swap',
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'Bold geometric headings with neutral body.',
    headingFont: 'Montserrat',
    bodyFont: 'Open Sans',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@700&family=Open+Sans:wght@400&display=swap',
  },
  {
    id: 'formal',
    name: 'Formal',
    description: 'Traditional serif pairing — dignified and classic.',
    headingFont: 'Libre Baskerville',
    bodyFont: 'Source Sans 3',
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@700&family=Source+Sans+3:wght@400&display=swap',
  },
];

// ─── Defaults ──────────────────────────────────────────────────────────────────

export const DEFAULT_THEME = {
  template_id: 'classic',
  palette_id: 'warm_default',
  font_pairing_id: 'playful',
};
